export const GENERAL_SR_TYPE_CODE = "0000";

export const getValueCaseInsensitive = (obj, candidateKeys = []) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of candidateKeys) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
  }
  const loweredMap = new Map(
    Object.keys(obj).map((key) => [String(key).toLowerCase(), key])
  );
  for (const k of candidateKeys) {
    const hit = loweredMap.get(String(k).toLowerCase());
    if (hit) return obj[hit];
  }
  return undefined;
};

export const parseSrTypeCodes = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim()).filter(Boolean);
  }
  return String(raw)
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
};

export const normalizeStoredSrTypeCode = (raw) => {
  const code = String(raw ?? "").trim();
  return code || GENERAL_SR_TYPE_CODE;
};

export const isGeneralSrTypeCode = (raw) =>
  normalizeStoredSrTypeCode(raw) === GENERAL_SR_TYPE_CODE;

/** Unique SR categories from invoice / material lines (view + raise). */
export const extractSrCategoriesFromInvoiceLines = (details, options = {}) => {
  const { childSrCode = null } = options;
  const childFilter = childSrCode ? String(childSrCode).trim() : "";
  const map = new Map();

  (details || []).forEach((d) => {
    if (d?.IsSelected === 0 || d?.isSelected === 0) return;

    if (childFilter) {
      const rowChild = String(
        d.ChildSRNumber ?? d.childSRNumber ?? d.ChildSrNumber ?? ""
      ).trim();
      if (rowChild && rowChild !== childFilter) return;
    }

    const code = String(
      d.SRTypeCode ?? d.srTypeCode ?? d.SRTYPECODE ?? ""
    ).trim();
    if (!code) return;

    const nameRaw =
      d.SRTypeName ??
      d.srTypeName ??
      d.SRTypeDesc ??
      d.srTypeDesc ??
      d.Category ??
      d.category ??
      "";
    const name =
      code === GENERAL_SR_TYPE_CODE
        ? "All"
        : String(nameRaw).trim() || code;

    if (!map.has(code)) map.set(code, name);
  });

  const entries = Array.from(map.entries()).map(([code, name]) => ({ code, name }));
  entries.sort((a, b) => {
    if (a.code === GENERAL_SR_TYPE_CODE) return -1;
    if (b.code === GENERAL_SR_TYPE_CODE) return 1;
    return a.name.localeCompare(b.name);
  });
  return entries;
};

export const isGeneralSrCategoryQuestion = (node) => {
  const codes = parseSrTypeCodes(node?.srTypeCode ?? node?.SRTypeCode ?? "");
  return codes.length === 0 || codes.includes(GENERAL_SR_TYPE_CODE);
};

export const filterGeneralQuestionTree = (nodes) => {
  const walk = (list) => {
    const out = [];
    (list || []).forEach((node) => {
      const filteredChildren = walk(node.children || []);
      if (isGeneralSrCategoryQuestion(node)) {
        out.push({ ...node, children: filteredChildren });
      } else if (filteredChildren.length > 0) {
        out.push({ ...node, children: filteredChildren });
      }
    });
    return out;
  };
  return walk(nodes);
};

const questionAppliesToSrCategoryOnly = (node, activeCode) => {
  if (!activeCode) return false;
  const codes = parseSrTypeCodes(node?.srTypeCode ?? node?.SRTypeCode ?? "");
  if (codes.length === 0) return false;
  if (codes.includes(GENERAL_SR_TYPE_CODE)) return false;
  return codes.includes(activeCode);
};

export const filterQuestionTreeBySrCategoryExcludingGeneral = (nodes, activeCode) => {
  if (!activeCode) return [];
  const walk = (list) => {
    const out = [];
    (list || []).forEach((node) => {
      const filteredChildren = walk(node.children || []);
      const selfMatches = questionAppliesToSrCategoryOnly(node, activeCode);
      if (selfMatches || filteredChildren.length > 0) {
        out.push({ ...node, children: filteredChildren });
      }
    });
    return out;
  };
  return walk(nodes);
};

export const looksLikeNumericChecklistAnswer = (value) =>
  /^-?\d+(\.\d+)?$/.test(String(value ?? "").trim());

const normalizeChecklistResponseValue = (row) => {
  const responseText =
    getValueCaseInsensitive(row, ["responseText", "ResponseText", "response"]) || "";
  const responseValue =
    getValueCaseInsensitive(row, ["responseValue", "ResponseValue"]) ?? null;
  const isBoolean =
    getValueCaseInsensitive(row, ["isBooleanResponse", "IsBooleanResponse"]) ?? false;

  let finalResponse = responseText;
  if (!finalResponse && responseValue !== null && responseValue !== undefined) {
    finalResponse = String(responseValue);
  }
  if (isBoolean && finalResponse !== "" && !looksLikeNumericChecklistAnswer(finalResponse)) {
    const normalized = String(finalResponse).toLowerCase();
    if (["true", "yes", "y"].includes(normalized)) finalResponse = "Yes";
    else if (["false", "no", "n"].includes(normalized)) finalResponse = "No";
  }
  return finalResponse;
};

/** Flatten a checklist question tree (from buildChecklistTree) into a list of nodes. */
export const flattenChecklistQuestionNodes = (nodes) => {
  const out = [];
  const walk = (list) => {
    (list || []).forEach((node) => {
      out.push(node);
      walk(node.children || []);
    });
  };
  walk(nodes);
  return out;
};

/** Map question code → sole non-general SR category when unambiguous (from checklist master). */
export const buildQuestionSrCategoryLookup = (flatQuestions = []) => {
  const lookup = new Map();
  (Array.isArray(flatQuestions) ? flatQuestions : []).forEach((q) => {
    const code = String(q?.questionCode ?? q?.QuestionCode ?? "").trim();
    if (!code) return;
    const categoryCodes = parseSrTypeCodes(q?.srTypeCode ?? q?.SRTypeCode ?? "").filter(
      (c) => c !== GENERAL_SR_TYPE_CODE
    );
    if (categoryCodes.length === 1) {
      lookup.set(code, categoryCodes[0]);
    }
  });
  return lookup;
};

/**
 * Before save/submit: move category-tagged answers out of templateAnswers (0000)
 * into srCategoryAnswers so SRCHECKLISTRESPONSES gets the correct SRTypeCode.
 */
export const partitionTemplateAnswersForStorage = (
  flatQuestions,
  generalAnswers = {},
  categoryAnswersByCode = {}
) => {
  const lookup = buildQuestionSrCategoryLookup(flatQuestions);
  const nextGeneral = { ...(generalAnswers || {}) };
  const nextCategory = {};
  Object.entries(categoryAnswersByCode || {}).forEach(([sr, map]) => {
    nextCategory[sr] = { ...(map || {}) };
  });

  if (!lookup.size) {
    return { generalAnswers: nextGeneral, categoryAnswersByCode: nextCategory };
  }

  Object.entries({ ...nextGeneral }).forEach(([qCode, val]) => {
    const inferredSr = lookup.get(qCode);
    if (!inferredSr) return;
    const existing = nextCategory[inferredSr]?.[qCode];
    if (existing != null && String(existing).trim() !== "") {
      delete nextGeneral[qCode];
      return;
    }
    if (!nextCategory[inferredSr]) nextCategory[inferredSr] = {};
    nextCategory[inferredSr][qCode] = val;
    delete nextGeneral[qCode];
  });

  return { generalAnswers: nextGeneral, categoryAnswersByCode: nextCategory };
};

/** Split saved checklist rows into general (0000) and per-tab category answers. */
export const splitChecklistResponsesBySrType = (rows = [], flatQuestions = null) => {
  const generalAnswers = {};
  const categoryAnswersByCode = {};
  const questionSrCategoryLookup =
    flatQuestions != null ? buildQuestionSrCategoryLookup(flatQuestions) : null;

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const questionCode =
      getValueCaseInsensitive(row, [
        "questionCode",
        "questionCODE",
        "QuestionCode",
        "QuestionCODE",
        "QUESTIONCODE",
      ]) || "";
    if (!questionCode) return;

    let srTypeCode = normalizeStoredSrTypeCode(
      getValueCaseInsensitive(row, ["srTypeCode", "SRTypeCode", "SRTYPECODE"])
    );
    const finalResponse = normalizeChecklistResponseValue(row);

    // Legacy rows saved as 0000 still belong on the SR Category tab when the question is category-tagged.
    if (
      questionSrCategoryLookup &&
      isGeneralSrTypeCode(srTypeCode) &&
      questionSrCategoryLookup.has(questionCode)
    ) {
      srTypeCode = questionSrCategoryLookup.get(questionCode);
    }

    if (isGeneralSrTypeCode(srTypeCode)) {
      generalAnswers[questionCode] = finalResponse;
    } else {
      if (!categoryAnswersByCode[srTypeCode]) categoryAnswersByCode[srTypeCode] = {};
      categoryAnswersByCode[srTypeCode][questionCode] = finalResponse;
    }
  });

  return { generalAnswers, categoryAnswersByCode };
};
